// Strapi v5.16.0 Integration
// Based on debug findings: direct field access, simple populate, singular category field

export const useStrapi = () => {
  const config = useRuntimeConfig();
  const strapiUrl =
    config.public.strapiUrl || "https://blog.desertando.anovagroup.es";
  const strapiApiToken = process.server ? config.strapiApiToken : null;

  const strapiRequest = async (endpoint, options = {}) => {
    const baseUrl = `${strapiUrl}/api`;
    const url = endpoint.startsWith("/")
      ? `${baseUrl}${endpoint}`
      : `${baseUrl}/${endpoint}`;

    const headers = {
      "Content-Type": "application/json",
      ...(strapiApiToken && { Authorization: `Bearer ${strapiApiToken}` }),
      ...options.headers,
    };

    try {
      const response = await $fetch(url, {
        ...options,
        headers,
      });

      return response;
    } catch (error) {
      console.error("❌ Strapi request failed:", {
        endpoint,
        url,
        status: error.status || error.statusCode,
        message: error.message,
      });
      throw error;
    }
  };

  // Get articles with Strapi v5 syntax
  const getArticles = async (options = {}) => {
    const {
      page = 1,
      pageSize = 9,
      category = null,
      populate = true,
    } = options;

    const params = new URLSearchParams();

    // Sorting
    params.append("sort[0]", "publishedAt:desc");

    // Pagination (Strapi v5 syntax)
    params.append("pagination[page]", page.toString());
    params.append("pagination[pageSize]", pageSize.toString());

    // Simple populate (works in v5)
    if (populate) {
      params.append("populate", "*");
    }

    // Category filter (singular field name based on debug)
    if (category) {
      params.append("filters[category][slug][$eq]", category);
    }

    try {
      const response = await strapiRequest(`/articles?${params.toString()}`);
      return response;
    } catch (error) {
      console.error("❌ Failed to fetch articles:", error);
      return { data: [], meta: { pagination: { total: 0 } } };
    }
  };

  // Get single article by slug
  const getArticle = async (slug) => {
    const params = new URLSearchParams();
    params.append("filters[slug][$eq]", slug);
    params.append("populate", "*");

    try {
      const response = await strapiRequest(`/articles?${params.toString()}`);
      return response?.data?.[0] || null;
    } catch (error) {
      console.error("❌ Failed to fetch article:", error);
      return null;
    }
  };

  // Get categories
  const getCategories = async () => {
    try {
      const response = await strapiRequest("/categories?populate=*");

      return response;
    } catch (error) {
      console.error("❌ Failed to fetch categories:", error);
      return { data: [], meta: {} };
    }
  };

  // Get related articles
  const getRelatedArticles = async (
    currentSlug,
    categorySlug = null,
    limit = 3
  ) => {
    const params = new URLSearchParams();
    params.append("sort[0]", "publishedAt:desc");
    params.append("pagination[pageSize]", limit.toString());
    params.append("populate", "*");

    // Exclude current article
    params.append("filters[slug][$ne]", currentSlug);

    // Filter by category if provided
    if (categorySlug) {
      params.append("filters[category][slug][$eq]", categorySlug);
    }

    try {
      const response = await strapiRequest(`/articles?${params.toString()}`);
      return response?.data || [];
    } catch (error) {
      console.error("❌ Failed to fetch related articles:", error);
      return [];
    }
  };

  return {
    strapiRequest,
    getArticles,
    getArticle,
    getCategories,
    getRelatedArticles,
  };
};
